import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import TopBar from "@/components/TopBar";
import ReferralChip from "@/components/ReferralChip";
import api, { streamUrl } from "@/lib/api";
import { catLabel } from "@/lib/constants";
import { Heart, Bookmark, Share2, Flag, Zap, Volume2, VolumeX, ShieldCheck, Sparkles, FileVideo, MessageSquare, UserPlus, UserCheck, EyeOff } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useAudio } from "@/lib/audioContext";
import { toast } from "sonner";
import CastingInquiryModal from "@/components/CastingInquiryModal";
import SponsoredBanner from "@/components/SponsoredBanner";

// Feed scroll position is persisted in sessionStorage under this key so that
// when the user navigates into a profile or video detail and comes back, the
// feed jumps back to the exact card they were watching.
const FEED_SCROLL_KEY = "ns_feed_scroll_y";

export default function Home() {
  const { user } = useAuth();
  const location = useLocation();
  const [videos, setVideos] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const PAGE = 20;
  const sentinel = useRef(null);
  const scrollBoxRef = useRef(null);

  const load = async (reset = false) => {
    if (reset) { setLoading(true); setSkip(0); setHasMore(true); }
    try {
      const [feed, feat] = await Promise.all([
        api.get(`/videos/feed?skip=0&limit=${PAGE}&strategy=smart`),
        api.get("/users/featured"),
      ]);
      setVideos(feed.data.videos || []);
      setHasMore(!!feed.data.has_more);
      setSkip((feed.data.videos || []).length);
      setFeatured(feat.data.users || []);
    } catch {
      setVideos([]); setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/videos/feed?skip=${skip}&limit=${PAGE}&strategy=smart`);
      const fresh = (data.videos || []).filter(v => !videos.find(x => x.id === v.id));
      setVideos((prev) => [...prev, ...fresh]);
      setHasMore(!!data.has_more);
      setSkip((prev) => prev + fresh.length);
    } catch {} finally { setLoadingMore(false); }
  };

  useEffect(() => { load(true); /* eslint-disable-next-line */ }, []);

  // Restore feed scroll position when the user returns from a profile / video
  // detail page. We store the last scroll offset on unmount and on every
  // scroll event (debounced via requestAnimationFrame).
  useEffect(() => {
    if (loading || !scrollBoxRef.current) return;
    const stored = parseInt(sessionStorage.getItem(FEED_SCROLL_KEY) || "0", 10);
    if (stored > 0) {
      // Wait a tick for the videos to render before scrolling
      requestAnimationFrame(() => {
        if (scrollBoxRef.current) scrollBoxRef.current.scrollTop = stored;
        window.scrollTo(0, stored);
      });
    }
  }, [loading, videos.length]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = scrollBoxRef.current?.scrollTop ?? window.scrollY;
        sessionStorage.setItem(FEED_SCROLL_KEY, String(y));
        ticking = false;
      });
    };
    const box = scrollBoxRef.current;
    box?.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      box?.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScroll);
    };
  }, [loading]);

  // Reset scroll memory when we leave the /app route (so navigating to another
  // section starts at top) but preserve it for /u/* and /v/* deep links.
  useEffect(() => () => {
    const dest = window.location.pathname;
    if (!dest.startsWith("/u/") && !dest.startsWith("/v/") && !dest.startsWith("/app")) {
      sessionStorage.removeItem(FEED_SCROLL_KEY);
    }
  }, [location.pathname]);

  // Infinite scroll: observe the sentinel div
  useEffect(() => {
    if (!sentinel.current) return;
    const el = sentinel.current;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "800px" });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line
  }, [videos.length, hasMore, loadingMore]);

  return (
    <div data-testid="home-page" className="min-h-screen bg-black">
      <TopBar/>

      {/* Floating top-right invite chip */}
      <div className="fixed top-16 right-3 z-30 pointer-events-none">
        <div className="pointer-events-auto flex flex-col items-end gap-2">
          <ReferralChip isAuthed={!!user}/>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[70vh] text-zinc-400 text-sm">Loading feed…</div>
      ) : videos.length === 0 ? (
        <EmptyFeed featured={featured}/>
      ) : (
        <>
          {featured.length > 0 && <FeaturedStrip featured={featured}/>}
          <div ref={scrollBoxRef} className="feed-container" data-testid="feed-container">
            {videos.map((v, idx) => (
              <FeedItemWrap key={v.id}>
                <FeedItem video={v} me={user} onChange={() => load(true)}/>
                {/* Mid-feed sponsored injection after every 7 videos (iter 44: was every 9, moved from top-of-feed) */}
                {((idx + 1) % 7 === 0) && (
                  <MidFeedAd slotIdx={idx + 1}/>
                )}
              </FeedItemWrap>
            ))}
            <div ref={sentinel} data-testid="feed-sentinel" className="col-span-full h-24 flex items-center justify-center text-zinc-500 text-xs bg-black">
              {loadingMore ? "Loading more…" : hasMore ? "Scroll for more" : "End of feed"}
            </div>
            {!hasMore && (
              <div className="col-span-full max-w-2xl mx-auto px-4 py-6 bg-black">
                <SponsoredBanner placement="bottom_banner"/>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Wraps each feed item — on mobile it's a plain div, on desktop it becomes
 *  a grid cell. Kept as its own component so mid-feed ad spanning is easy. */
function FeedItemWrap({ children }) {
  return <>{children}</>;
}

function MidFeedAd({ slotIdx }) {
  return (
    <div data-testid={`mid-feed-slot-${slotIdx}`} className="feed-item grid-full max-w-2xl mx-auto w-full px-4 py-4 bg-black" style={{ aspectRatio: 'auto' }}>
      <SponsoredBanner placement="mid_feed"/>
    </div>
  );
}

function FeaturedStrip({ featured }) {
  return (
    <div data-testid="featured-strip" className="absolute top-14 left-0 right-0 z-20 pointer-events-none">
      <div className="pointer-events-auto max-w-2xl mx-auto px-3 pt-3">
        <div className="glass rounded-2xl border border-zinc-800/80 px-3 py-2 overflow-x-auto flex items-center gap-3 [&::-webkit-scrollbar]:hidden">
          <span className="text-[10px] uppercase tracking-[0.15em] text-[#DC2626] flex items-center gap-1 shrink-0">
            <Sparkles size={11}/> Featured
          </span>
          {featured.slice(0, 8).map((u) => (
            <Link key={u.user_id} to={`/u/${u.username}`} data-testid={`featured-${u.username}`} className="shrink-0 flex items-center gap-2 rounded-full bg-zinc-900/60 border border-zinc-800 px-2 py-1 hover:border-[#DC2626]/40 transition-colors">
              <div className="w-6 h-6 rounded-full bg-[#DC2626]/20 border border-[#DC2626]/40 flex items-center justify-center text-[#FCA5A5] text-[10px] font-bold">
                {(u.name || "?").slice(0,1).toUpperCase()}
              </div>
              <span className="text-xs text-white whitespace-nowrap">{(u.name || u.username).split(' ')[0]}</span>
              {u.is_verified && <ShieldCheck size={10} className="text-[#F5C462]"/>}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyFeed({ featured }) {
  const chips = [
    "Actor", "Singer", "Dancer", "Comedian", "Model", "Poet",
    "Voice Artist", "Photographer", "Musician", "Storyteller"
  ];
  return (
    <div data-testid="empty-feed" className="max-w-2xl mx-auto px-6 pt-6 pb-24">
      <div className="rounded-3xl border border-[#DC2626]/30 bg-gradient-to-br from-[#DC2626]/12 via-[#180D0D] to-[#0A0605] p-8 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#DC2626]/25 blur-3xl"/>
        <p className="text-xs uppercase tracking-[0.2em] text-[#DC2626]">Curtain up</p>
        <h2 className="mt-2 font-display text-3xl font-bold leading-tight">Bharat ka stage <span className="gold-text">taiyaar hai.</span></h2>
        <p className="mt-3 text-sm text-zinc-300 leading-relaxed">Upload your first 30-second act — casting directors, mentors and fans are watching.</p>
        <Link to="/app/upload" data-testid="empty-feed-upload" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold px-6 h-11 transition-colors">
          Upload your first performance
        </Link>
      </div>

      {featured.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-[#DC2626]"/>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">Talent of the week</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {featured.slice(0, 6).map((u) => (
              <Link key={u.user_id} to={`/u/${u.username}`} data-testid={`talent-week-${u.username}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 hover:border-[#DC2626]/40 transition-colors">
                <div className="w-12 h-12 rounded-full bg-[#DC2626]/20 border border-[#DC2626]/40 flex items-center justify-center text-[#FCA5A5] text-lg font-bold">
                  {(u.name || "?").slice(0,1).toUpperCase()}
                </div>
                <div className="mt-2 flex items-center gap-1">
                  <p className="font-semibold text-sm truncate">{u.name}</p>
                  {u.is_verified && <ShieldCheck size={12} className="text-[#F5C462] shrink-0"/>}
                </div>
                <p className="text-xs text-[#DC2626] truncate">{u.category?.replace(/-/g, ' ')}</p>
                {u.profile?.city && <p className="text-xs text-zinc-400 truncate mt-0.5">{u.profile.city}</p>}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Explore by craft</p>
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <Link key={c} to={`/app/discover`} data-testid={`chip-${c.toLowerCase().replace(/\s/g,'-')}`} className="rounded-full border border-zinc-800 bg-[#180D0D] px-4 h-9 inline-flex items-center text-sm text-zinc-300 hover:border-[#DC2626]/40 hover:text-white transition-colors">
              {c}
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-3">
        <Link to="/app/discover" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5 hover:border-[#DC2626]/40 transition-colors">
          <p className="font-display text-lg font-semibold">Discover talent</p>
          <p className="text-xs text-zinc-400 mt-1">Search, filter by city and craft</p>
        </Link>
        <Link to="/app/plans" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5 hover:border-[#DC2626]/40 transition-colors">
          <p className="font-display text-lg font-semibold">Unlock casting</p>
          <p className="text-xs text-zinc-400 mt-1">Plus & Pro get role alerts</p>
        </Link>
      </div>
    </div>
  );
}

function FeedItem({ video, me, onChange }) {
  const { audioEnabled } = useAudio();
  const [bookmarked, setBookmarked] = useState(me?.bookmarks?.includes(video.id));
  const [likes, setLikes] = useState(video.likes || 0);
  const [liked, setLiked] = useState(false);
  const [castOpen, setCastOpen] = useState(false);
  const [following, setFollowing] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentsCount, setCommentsCount] = useState(video.comments_count || 0);
  // Iter 44: per-video mute. Iter 45: default derived from global AudioContext
  // — once the visitor has clicked ANYWHERE on the site, every autoplaying
  // video begins with sound on. Users can still override per-tile.
  const [muted, setMuted] = useState(!audioEnabled);
  // If the global audio flag flips (first interaction elsewhere in the app),
  // auto-unmute any tile the user hasn't manually re-muted.
  const [userTouched, setUserTouched] = useState(false);
  useEffect(() => {
    if (audioEnabled && !userTouched) setMuted(false);
  }, [audioEnabled, userTouched]);
  const ref = useRef(null);
  const onToggleMute = (e) => {
    e?.stopPropagation?.();
    setUserTouched(true);
    setMuted((m) => {
      const next = !m;
      requestAnimationFrame(() => { ref.current?.play?.().catch(() => {}); });
      return next;
    });
  };

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) el.play().catch(() => {});
      else el.pause();
    }, { threshold: 0.6 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const like = async () => {
    try {
      const { data } = await api.post(`/videos/${video.id}/like`);
      setLiked(data.liked);
      setLikes(l => l + (data.liked ? 1 : -1));
    } catch {}
  };
  const notInterested = async () => {
    try {
      await api.post(`/videos/${video.id}/not-interested`);
      toast.success("Got it — showing you less of this");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const bookmark = async () => {
    try {
      const { data } = await api.post(`/videos/${video.id}/bookmark`);
      setBookmarked(data.bookmarked);
      toast(data.bookmarked ? "Saved" : "Removed");
    } catch {}
  };
  const follow = async () => {
    if (!me) { toast.error("Log in to follow"); return; }
    if (video.user_id === me.user_id) return;
    try {
      const { data } = await api.post(`/users/${video.username}/follow`);
      setFollowing(data.following);
      toast.success(data.following ? `Following @${video.username}` : "Unfollowed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openComments = async () => {
    setShowComments(true);
    try {
      const { data } = await api.get(`/videos/${video.id}/comments`);
      setComments(data.comments || []);
    } catch {}
  };
  const postComment = async (e) => {
    e.preventDefault();
    const text = commentText.trim();
    if (!text) return;
    try {
      const { data } = await api.post(`/videos/${video.id}/comment`, { text });
      setComments((prev) => [data.comment, ...prev]);
      setCommentText("");
      setCommentsCount(c => c + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || "Comment failed"); }
  };
  const [reportOpen, setReportOpen] = useState(false);
  const report = () => setReportOpen(true);
  const submitReport = async (reason) => {
    setReportOpen(false);
    try { await api.post(`/videos/${video.id}/report`, { reason }); toast.success("Thank you. Our team will review."); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const share = async () => {
    const url = `${window.location.origin}/v/${video.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: video.title || "NextScene", url }); return; } catch {}
    }
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div data-testid={`feed-item-${video.id}`} className="feed-item relative bg-black">
      <video
        ref={ref}
        data-testid="feed-video"
        src={streamUrl(video.storage_path)}
        muted={muted}
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* dark gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent pointer-events-none"/>

      {/* Right actions column — mute moved to the TOP of this column so it
          never overlaps the Like row on tighter grid tiles. */}
      <div className="absolute right-3 top-4 bottom-32 z-10 flex flex-col gap-4 items-center">
        <ActionBtn testid="mute-btn" onClick={onToggleMute}>
          {muted ? <VolumeX size={18}/> : <Volume2 size={18}/>}
        </ActionBtn>
        <div className="flex-1"/>
        <ActionBtn testid="like-btn" onClick={like} active={liked}><Heart size={22} fill={liked ? "#EF4444" : "none"}/></ActionBtn>
        <span className="text-xs text-white -mt-3">{likes}</span>
        {video.user_id !== me?.user_id && (
          <ActionBtn testid="not-interested-btn" onClick={notInterested} title="Not interested"><EyeOff size={20}/></ActionBtn>
        )}
        <Link to={`/v/${video.id}`} className="w-11 h-11 rounded-full glass border border-zinc-700 flex items-center justify-center text-white" data-testid="video-details-btn" title="Open">
          <FileVideo size={18}/>
        </Link>
        <ActionBtn testid="save-btn" onClick={bookmark} active={bookmarked}><Bookmark size={20} fill={bookmarked ? "#DC2626" : "none"} color={bookmarked ? "#DC2626" : "#fff"}/></ActionBtn>
        <ActionBtn testid="share-btn" onClick={share}><Share2 size={20}/></ActionBtn>
        {video.user_id !== me?.user_id && (
          <ActionBtn testid="follow-btn" onClick={follow} active={following}>
            {following ? <UserCheck size={20} color="#F5C462"/> : <UserPlus size={20}/>}
          </ActionBtn>
        )}
        <ActionBtn testid="report-btn" onClick={report}><Flag size={18}/></ActionBtn>
        {video.user_id !== me?.user_id && (
          <button
            data-testid="feed-cast-btn"
            onClick={() => setCastOpen(true)}
            title="Cast this talent"
            className="w-11 h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] border-2 border-white/20 text-white flex items-center justify-center shadow-lg"
          >
            <MessageSquare size={18}/>
          </button>
        )}
      </div>

      <CastingInquiryModal open={castOpen} target={{ username: video.username, video_id: video.id }} onClose={() => setCastOpen(false)}/>

      {reportOpen && (
        <div data-testid="report-modal" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4" onClick={() => setReportOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-[#0A0605] p-4 space-y-2">
            <p className="text-white font-semibold">Why are you reporting this?</p>
            <p className="text-[11px] text-zinc-400 mb-2">Child safety reports are prioritised and auto-hide the video immediately.</p>
            {[
              {k: "copyright", label: "Copyright violation"},
              {k: "spam", label: "Spam or misleading"},
              {k: "abusive", label: "Abusive or hateful"},
              {k: "nudity", label: "Nudity or sexual content"},
              {k: "child_safety", label: "Child safety concern", danger: true},
            ].map(o => (
              <button key={o.k} data-testid={`report-reason-${o.k}`} onClick={() => submitReport(o.k)} className={`w-full text-left rounded-xl border p-3 text-sm transition-colors ${o.danger ? "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20" : "border-zinc-800 bg-zinc-900 text-white hover:border-[#DC2626]/40"}`}>
                {o.label}
              </button>
            ))}
            <button onClick={() => setReportOpen(false)} className="w-full text-center text-xs text-zinc-500 pt-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Bottom info */}
      <div className="absolute left-0 right-14 bottom-0 z-10 p-4 pb-20 text-white">
        <Link to={`/${video.username}`} className="inline-flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#DC2626]/20 border border-[#DC2626]/50 flex items-center justify-center text-[#DC2626] font-semibold">
            {(video.name || "?").slice(0,1).toUpperCase()}
          </div>
          <div className="leading-tight">
            <p className="text-sm font-semibold">{video.name}</p>
            <p className="text-xs text-zinc-400">@{video.username} · {catLabel(video.category)}</p>
          </div>
        </Link>
        {video.title && <h3 className="mt-3 font-display text-lg font-semibold">{video.title}</h3>}
        {video.description && <p className="mt-1 text-sm text-zinc-300 line-clamp-2">{video.description}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to={`/app/upload?challenge=${video.id}`}
            data-testid="challenge-this-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#DC2626] text-white font-semibold px-4 h-9 pulse-gold hover:bg-[#EF4444] transition-colors"
          >
            <Zap size={14}/> Challenge this
          </Link>
          {video.user_id !== me?.user_id && (
            <button
              onClick={() => setCastOpen(true)}
              data-testid="feed-cast-inline"
              className="inline-flex items-center gap-2 rounded-full bg-black/60 border border-[#DC2626]/50 text-white font-semibold px-4 h-9 hover:bg-[#DC2626]/20 transition-colors"
            >
              <MessageSquare size={14}/> Cast this talent
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, active, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`w-11 h-11 rounded-full glass border ${active ? "border-[#DC2626]" : "border-zinc-700"} flex items-center justify-center text-white transition-colors duration-200`}>
      {children}
    </button>
  );
}
